import { useTranslation } from 'react-i18next';

import { useEventDetailsCache, useEventStatus, useIsAllEventsFinished } from '@/hooks/use-sbk-event-details-cache';
import { renderDate } from '@/utils/renderDate';
import { formatMatchupString, formatScoredMatchupString } from '@/utils/sport-utils';
import { format } from 'date-fns';

import { Bet, BetEvent } from './types';

/** "{label}: {M/d/yy} {h:mm a}" — the date line shown in a bet card footer. */
const formatDateLine = (label: string, timestamp: number) => {
    const date = new Date(timestamp);
    return `${label}: ${format(date, 'M/d/yy')} ${format(date, 'h:mm a')}`;
};

/**
 * Footer date line for an open bet — when it was placed.
 */
export const useFooter = (bet: Bet) => {
    const { t } = useTranslation('bets');
    return formatDateLine(t('placed'), bet.created_at);
};

/**
 * Footer date line for a settled bet — when it was settled.
 */
export const useSettledFooter = (bet: Bet) => {
    const { t } = useTranslation('bets');
    return formatDateLine(t('settled'), bet.settled_at);
};

/*
 * Get the market status for an event
 * Returns the score if the event is live or finished
 */
export const useMarketStatus = (
    event: Pick<BetEvent, 'id' | 'home_team' | 'away_team' | 'event_details' | 'sport'>
) => {
    const eventStatus = useEventStatus(event.id);
    const homeScore =
        useEventDetailsCache(state => state.eventDetails[event.id]?.home_score) ?? event.event_details?.home_score;
    const awayScore =
        useEventDetailsCache(state => state.eventDetails[event.id]?.away_score) ?? event.event_details?.away_score;

    const sportName = event.sport?.name ?? '';

    if (eventStatus === 'LIVE' || eventStatus === 'FINISHED') {
        return formatScoredMatchupString(
            event.home_team?.short_code,
            event.away_team?.short_code,
            homeScore,
            awayScore,
            sportName
        );
    }

    return formatMatchupString(event.home_team?.short_code, event.away_team?.short_code, sportName);
};

export const useGameStatus = (bet: Bet) => {
    const { t } = useTranslation('common');
    const isFinished = useIsAllEventsFinished(bet.events.map(event => event.id));
    const startTime = renderDate(new Date(bet.events[0].start_time));
    //todo: add Final OT logic
    return isFinished ? `${t('final')}` : startTime;
};
